import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import type { PackingRow } from './packingApi';
import type { PackingAction } from './PackingActionModal';
import { badge, btn, nowrap, num, td, th, visuallyHidden } from './packingStyles';

interface Props {
  rows: PackingRow[];
  isManager: boolean;
  onAction: (action: PackingAction, row: PackingRow) => void;
  onToggleCartons: (row: PackingRow) => void;
  onClearLabels: (row: PackingRow) => void;
  onDetails: (row: PackingRow) => void;
}

/** Overdue rows are strongly red, priority-note rows orange, rows without cartons grey until cartons are back. */
const rowBackground = (row: PackingRow): string => {
  if (row.no_cartons) return '#e5e7eb';
  if (row.overdue) return '#fee2e2';
  if (row.priority_note) return '#fff7ed';
  return '#ffffff';
};

const rowEdge = (row: PackingRow): string | undefined => {
  if (row.no_cartons) return 'inset 5px 0 0 #6b7280';
  if (row.overdue) return 'inset 5px 0 0 #dc2626';
  if (row.priority_note) return 'inset 5px 0 0 #ea580c';
  return undefined;
};

/** Quantity columns share one fixed width, so the numbers line up and nothing is squeezed. */
const QTY_WIDTH = 96;

/** Narrower quantity columns on small screens leave room for the product name. */
const QTY_WIDTH_COMPACT = 76;

/** Below this width the parameters move into the product cell, so every quantity column stays in view. */
const COMPACT_BELOW = 1100;

const useCompact = (): boolean => {
  const [compact, setCompact] = useState(() => window.innerWidth < COMPACT_BELOW);
  useEffect(() => {
    const onResize = () => setCompact(window.innerWidth < COMPACT_BELOW);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return compact;
};

/** "PLA 1.75 · 1 kg": material, diameter, weight (the spool and the brand are shown on their own). */
const paramsLine = (row: PackingRow): string =>
  [[row.material, row.srednica].filter(Boolean).join(' '), row.waga ? `${row.waga} kg` : null].filter(Boolean).join(' · ');

/** The spool marking decides which carton and label are used — large, so it is never mixed up. */
const spoolStyle: React.CSSProperties = { fontSize: 18, fontWeight: 800, color: '#3730a3', lineHeight: 1.2, whiteSpace: 'nowrap' };

const brandStyle: React.CSSProperties = { fontSize: 16, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 };

interface MenuState {
  id: number;
  right: number;
  top: number;
}

const PackingRowsTable: React.FC<Props> = ({ rows, isManager, onAction, onToggleCartons, onClearLabels, onDetails }) => {
  const { t } = useLanguage();
  const compact = useCompact();
  const qtyWidth = compact ? QTY_WIDTH_COMPACT : QTY_WIDTH;
  // Fixed-position menu in a portal: an in-cell popover would be clipped by the table's overflow box.
  const [menu, setMenu] = useState<MenuState | null>(null);
  const openRow = menu ? rows.find((r) => r.pivot_id === menu.id) ?? null : null;

  useEffect(() => {
    if (menu === null) return;
    const close = () => setMenu(null);
    document.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menu]);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, row: PackingRow) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const top = Math.min(r.bottom + 4, window.innerHeight - 280);
    setMenu(menu?.id === row.pivot_id ? null : { id: row.pivot_id, right: window.innerWidth - r.right, top });
  };

  const menuItem = (label: string, onClick: () => void, disabled = false) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => { setMenu(null); onClick(); }}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', background: '#fff', fontSize: 15, cursor: disabled ? 'default' : 'pointer', color: disabled ? '#cbd5e1' : '#1e293b', borderTop: '1px solid #f1f5f9' }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 1, ...nowrap, paddingLeft: 14 }}>{t('packing.col.weekLine')}</th>
            <th style={th}>{t('packing.col.product')}</th>
            {!compact && <th style={{ ...th, width: 1, ...nowrap }}>{t('packing.col.params')}</th>}
            <th style={{ ...th, width: 1, ...nowrap }}>{t('packing.col.brand')}</th>
            <th style={{ ...th, width: qtyWidth, textAlign: 'right' }}>{t('packing.col.arrived')}</th>
            <th style={{ ...th, width: qtyWidth, textAlign: 'right', color: '#4338ca' }}>{t('packing.col.toPack')}</th>
            <th style={{ ...th, width: qtyWidth, textAlign: 'right' }}>{t('packing.col.packed')}</th>
            <th style={{ ...th, width: qtyWidth, textAlign: 'right' }}>{t('packing.col.defects')}</th>
            <th style={{ ...th, width: 1 }}><span style={visuallyHidden}>{t('packing.col.actions')}</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const muted = row.no_cartons ? { color: '#64748b' } : {};
            const sub: React.CSSProperties = { fontSize: 12, color: '#64748b', marginTop: 2 };
            const params = paramsLine(row);
            return (
              <tr key={row.pivot_id} style={{ background: rowBackground(row), boxShadow: rowEdge(row) }}>
                <td style={{ ...td, ...muted, ...nowrap, paddingLeft: 14 }}>
                  <div style={{ fontWeight: 700 }}>{t('packing.col.week')} {row.tydzien}</div>
                  <div style={sub}>{t('packing.col.line')} {row.linia}</div>
                  {row.location && <div style={sub}>{row.location}</div>}
                </td>
                <td style={{ ...td, ...muted }}>
                  <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>{row.nazwa}</div>
                  <div style={{ ...sub, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                    {row.priority_note && <span style={badge('#ea580c', '#fff')}>{t('packing.badge.priority')}</span>}
                    {row.overdue && <span style={badge('#dc2626', '#fff')}>{t('packing.badge.overdue')}</span>}
                    {row.no_cartons && <span style={badge('#64748b', '#fff')}>{t('packing.badge.noCartons')}</span>}
                    {row.labels_missing_qty != null && (
                      <span style={badge('#f59e0b', '#fff')}>{t('packing.badge.labels', { qty: row.labels_missing_qty })}</span>
                    )}
                    <span style={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                      {[row.indeks, row.ean].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  {compact && (
                    <>
                      {row.szpula && <div style={{ ...spoolStyle, marginTop: 4 }}>{row.szpula}</div>}
                      {params && <div style={sub}>{params}</div>}
                    </>
                  )}
                  {row.priority_note && <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600, marginTop: 2 }}>{row.priority_note}</div>}
                </td>
                {!compact && (
                  <td style={{ ...td, ...muted, ...nowrap }}>
                    {row.szpula && <div style={{ ...spoolStyle, ...muted }}>{row.szpula}</div>}
                    {params && <div style={sub}>{params}</div>}
                  </td>
                )}
                <td style={{ ...td, ...muted }}>
                  <div style={{ ...brandStyle, ...muted }}>{row.marka || '—'}</div>
                </td>
                <td style={{ ...num, ...muted }}>{row.arrived}</td>
                <td style={{ ...num, fontSize: 22, fontWeight: 800, color: row.no_cartons ? '#64748b' : '#4338ca' }}>{row.to_pack}</td>
                <td style={{ ...num, ...muted }}>{row.packed}</td>
                <td style={{ ...num, ...muted, color: row.defects ? '#b91c1c' : muted.color }}>{row.defects}</td>
                <td style={{ ...td, ...nowrap, paddingRight: 12 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      style={{ ...btn('primary'), minWidth: compact ? 80 : 96, opacity: row.to_pack < 1 ? 0.5 : 1 }}
                      disabled={row.to_pack < 1}
                      onClick={() => onAction('pack', row)}
                    >
                      {t('packing.action.pack')}
                    </button>
                    <button
                      type="button"
                      aria-haspopup="menu"
                      aria-expanded={menu?.id === row.pivot_id}
                      aria-label={t('packing.col.actions')}
                      style={{ ...btn('ghost'), minWidth: 44, padding: '10px 12px', fontSize: 18, lineHeight: 1 }}
                      onClick={(e) => openMenu(e, row)}
                    >
                      ⋯
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {menu && openRow && createPortal(
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', right: menu.right, top: menu.top, zIndex: 1050, minWidth: 210, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 10px 24px rgba(15,23,42,0.18)', overflow: 'hidden' }}
        >
          {menuItem(t('packing.action.defect'), () => onAction('defect', openRow), openRow.to_pack < 1)}
          {menuItem(openRow.no_cartons ? t('packing.action.cartonsBack') : t('packing.action.noCartons'), () => onToggleCartons(openRow))}
          {menuItem(
            openRow.labels_missing_qty != null ? t('packing.action.labelsClear') : t('packing.action.labels'),
            () => (openRow.labels_missing_qty != null ? onClearLabels(openRow) : onAction('labels', openRow)),
          )}
          {isManager && menuItem(t('packing.action.priority'), () => onAction('priority', openRow))}
          {menuItem(t('packing.action.details'), () => onDetails(openRow))}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default PackingRowsTable;
